import React from "react";
import aivimartLogo from "../../assets/logo2.png";

const CODE39 = {
  '0': '000110100', '1': '100100001', '2': '001100001', '3': '101100000',
  '4': '000110001', '5': '100110000', '6': '001110000', '7': '000100101',
  '8': '100100100', '9': '001100100', 'A': '100001001', 'B': '001001001',
  'C': '101001000', 'D': '000011001', 'E': '100011000', 'F': '001011000',
  'G': '000001101', 'H': '100001100', 'I': '001001100', 'J': '000011100',
  'K': '100000011', 'L': '001000011', 'M': '101000010', 'N': '000010011',
  'O': '100010010', 'P': '001010010', 'Q': '000000111', 'R': '100000110',
  'S': '001000110', 'T': '000010110', 'U': '110000001', 'V': '011000001',
  'W': '111000000', 'X': '010010001', 'Y': '110010000', 'Z': '011010000',
  '-': '010000101', '.': '110000100', ' ': '011000100', '$': '010101000',
  '/': '010100010', '+': '010001010', '%': '000101010', '*': '010010100'
};

const Barcode = ({ value }) => {
  const clean = '*' + String(value || 'AIVIMART').toUpperCase().replace(/[^0-9A-Z\-\. \$\/\+\%]/g, '') + '*';
  const bars = [];
  let currentX = 5;

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    const pattern = CODE39[ch] || CODE39['-'];
    for (let p = 0; p < 9; p++) {
      const isBar = p % 2 === 0;
      const width = pattern[p] === '1' ? 2.5 : 1;
      if (isBar) {
        bars.push({ x: currentX, width });
      }
      currentX += width;
    }
    currentX += 1;
  }

  const totalWidth = currentX + 5;

  return (
    <svg viewBox={'0 0 ' + totalWidth + ' 34'} style={{ width: '100%', height: '30px', display: 'block' }}>
      {bars.map((bar, idx) => (
        <rect key={idx} x={bar.x} y={0} width={bar.width} height={34} fill="#000" />
      ))}
    </svg>
  );
};

const ComponentToPrintLabel = React.forwardRef(({ orderData, orderDetails, shipment, pickupAddress }, ref) => {
  if (!orderData) return null;

  const items = orderDetails || [];
  const customerTotal = Number(orderData.overall_price || 0);
  const isOnline = orderData.payment_type === "Online" || orderData.payment_type == 1;

  const shipping = orderData.shippingAddress || {};
  const awbCode = shipment?.assigned_awb_code;
  const courierName = shipment?.assigned_courier_name || "Express Delivery";
  const barcodeValue = awbCode || orderData.order_uuid;

  const formatAddr = (addr) => {
    return [addr.address, addr.street, addr.land_mark, addr.city, addr.state].filter(Boolean).join(", ");
  };

  const totalQty = items.reduce((acc, i) => acc + Number(i.quantity || 1), 0);

  return (
    <div ref={ref} style={styles.labelWrapper}>
      <div style={styles.labelCard}>
        {/* ── Top Header: Brand + Payment Status ── */}
        <div style={styles.topRow}>
          <div>
            <img src={aivimartLogo} alt="Aivimart" style={styles.brandLogo} />
            <div style={styles.carrierSub}>Standard Express Logistics</div>
          </div>
          <div style={isOnline ? styles.prepaidBadge : styles.codBadge}>
            <div style={styles.payTypeHeader}>{isOnline ? "PREPAID" : "COD"}</div>
            {!isOnline ? (
              <div style={styles.codAmount}>COLLECT: ₹ {customerTotal.toFixed(2)}</div>
            ) : (
              <div style={styles.prepaidSub}>DO NOT COLLECT CASH</div>
            )}
          </div>
        </div>

        {/* ── Courier & Tracking / Barcode ── */}
        <div style={styles.barcodeSection}>
          <div style={styles.courierRow}>
            <span><strong>Carrier:</strong> {courierName}</span>
            {awbCode && <span><strong>AWB:</strong> {awbCode}</span>}
          </div>
          <div style={styles.barcodeWrapper}>
            <Barcode value={barcodeValue} />
            <div style={styles.barcodeText}>{barcodeValue}</div>
          </div>
        </div>

        {/* ── SHIP TO / DELIVER TO (High-Priority Box) ── */}
        <div style={styles.shipToBox}>
          <div style={styles.sectionHeader}>DELIVER TO:</div>
          <div style={styles.customerName}>
            {shipping.user_name || orderData.user_name || "Customer"}
          </div>
          <div style={styles.addressBody}>
            {formatAddr(shipping) || "No Address Provided"}
          </div>
          <div style={styles.pinAndContactRow}>
            <div style={styles.pinHighlight}>
              PIN: {shipping.pin || "—"}
            </div>
            {(shipping.mobile || orderData.mobile) && (
              <div style={styles.contactPhone}>
                📞 {shipping.mobile || orderData.mobile}
              </div>
            )}
          </div>
        </div>

        {/* ── SHIP FROM / RETURN ADDRESS ── */}
        <div style={styles.shipFromBox}>
          <div style={styles.sectionHeaderSmall}>RETURN ADDRESS / SOLD BY:</div>
          <div style={styles.sellerName}>
            {pickupAddress?.pickup_code ? pickupAddress.pickup_code + " (Aivimart Seller Hub)" : "Aivimart Fulfillment Center"}
          </div>
          <div style={styles.sellerAddr}>
            {pickupAddress ? (
              [pickupAddress.address, pickupAddress.city, pickupAddress.state, pickupAddress.pin_code].filter(Boolean).join(", ")
            ) : (
              "Aivimart Logistics, Support: Aivimart8@gmail.com | https://aivimart.com"
            )}
          </div>
          {pickupAddress?.phone && (
            <div style={styles.sellerPhone}>Phone: {pickupAddress.phone}</div>
          )}
        </div>

        {/* ── Package Details & Order Contents ── */}
        <div style={styles.detailsRow}>
          <div style={styles.detailCol}>
            <strong>Order ID:</strong> {orderData.order_uuid}
          </div>
          <div style={styles.detailCol}>
            <strong>Date:</strong> {orderData.order_date || "—"}
          </div>
          <div style={styles.detailCol}>
            <strong>Total Qty:</strong> {totalQty}
          </div>
        </div>

        <div style={styles.itemsSummary}>
          <div style={styles.itemsHeader}>PACKAGE CONTENTS:</div>
          <div style={styles.itemsList}>
            {items.slice(0, 3).map((it, idx) => (
              <div key={idx} style={styles.itemLine}>
                • {it.product_name} <strong>(Qty: {it.quantity})</strong>
              </div>
            ))}
            {items.length > 3 && (
              <div style={{ fontSize: "9px", color: "#666", fontStyle: "italic" }}>
                + {items.length - 3} more item(s)...
              </div>
            )}
          </div>
        </div>

        {/* ── Footer / Instructions ── */}
        <div style={styles.footerRow}>
          <div>HANDLE WITH CARE • FRAGILE</div>
          <div>If undelivered, please return to seller.</div>
        </div>
      </div>
    </div>
  );
});

export default ComponentToPrintLabel;

const styles = {
  labelWrapper: {
    padding: "0",
    margin: "0 auto",
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#fff",
    pageBreakInside: "avoid",
    breakInside: "avoid",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  },
  labelCard: {
    width: "350px",
    border: "2px solid #000",
    borderRadius: "2px",
    padding: "8px 10px",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: "#000",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    pageBreakInside: "avoid",
    breakInside: "avoid",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "2px solid #000",
    paddingBottom: "4px",
  },
  brandLogo: {
    height: "26px",
    width: "auto",
    objectFit: "contain",
    display: "block",
  },
  carrierSub: {
    fontSize: "8px",
    fontWeight: "700",
    color: "#555",
    textTransform: "uppercase",
    marginTop: "1px",
    letterSpacing: "0.5px",
  },
  codBadge: {
    border: "2px solid #000",
    backgroundColor: "#fff",
    padding: "2px 6px",
    textAlign: "center",
    borderRadius: "2px",
  },
  prepaidBadge: {
    border: "2px solid #000",
    backgroundColor: "#000",
    color: "#fff",
    padding: "2px 6px",
    textAlign: "center",
    borderRadius: "2px",
  },
  payTypeHeader: {
    fontSize: "13px",
    fontWeight: "900",
    letterSpacing: "1px",
    lineHeight: 1,
  },
  codAmount: {
    fontSize: "8.5px",
    fontWeight: "800",
    marginTop: "1px",
  },
  prepaidSub: {
    fontSize: "7.5px",
    fontWeight: "700",
    color: "#fff",
    marginTop: "1px",
  },
  barcodeSection: {
    textAlign: "center",
    borderBottom: "1px solid #000",
    paddingBottom: "3px",
  },
  courierRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "9.5px",
    marginBottom: "1px",
  },
  barcodeWrapper: {
    padding: "1px 0",
  },
  barcodeText: {
    fontSize: "10.5px",
    fontWeight: "700",
    letterSpacing: "1.5px",
    marginTop: "1px",
  },
  shipToBox: {
    border: "2px solid #000",
    padding: "5px 7px",
    backgroundColor: "#fff",
  },
  sectionHeader: {
    fontSize: "9.5px",
    fontWeight: "900",
    letterSpacing: "0.8px",
    marginBottom: "2px",
  },
  customerName: {
    fontSize: "13.5px",
    fontWeight: "800",
    marginBottom: "2px",
  },
  addressBody: {
    fontSize: "10.5px",
    lineHeight: "1.25",
    marginBottom: "3px",
  },
  pinAndContactRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: "1px dashed #666",
    paddingTop: "3px",
    marginTop: "2px",
  },
  pinHighlight: {
    fontSize: "14px",
    fontWeight: "900",
    letterSpacing: "0.5px",
  },
  contactPhone: {
    fontSize: "10.5px",
    fontWeight: "700",
  },
  shipFromBox: {
    border: "1px solid #666",
    padding: "4px 6px",
    fontSize: "9px",
    backgroundColor: "#fafafa",
  },
  sectionHeaderSmall: {
    fontSize: "8px",
    fontWeight: "800",
    color: "#444",
    marginBottom: "1px",
  },
  sellerName: {
    fontWeight: "700",
    fontSize: "9.5px",
    marginBottom: "1px",
  },
  sellerAddr: {
    color: "#444",
    lineHeight: "1.2",
  },
  sellerPhone: {
    marginTop: "1px",
    color: "#333",
    fontWeight: "600",
  },
  detailsRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "9px",
    borderBottom: "1px solid #ccc",
    paddingBottom: "2px",
  },
  detailCol: {
    flex: 1,
  },
  itemsSummary: {
    fontSize: "9px",
    borderBottom: "1px solid #000",
    paddingBottom: "3px",
  },
  itemsHeader: {
    fontWeight: "800",
    fontSize: "8px",
    marginBottom: "1px",
    color: "#444",
  },
  itemsList: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  itemLine: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  footerRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "8px",
    fontWeight: "700",
    color: "#333",
    paddingTop: "2px",
  },
};
